import { AlertTriangle, Clock, Image as ImageIcon, Pencil, Plus, Smile, Target, Trash2, TrendingDown, TrendingUp, X } from "lucide-react";
import { useState } from "react";
import JournalQuickNote from "./JournalQuickNote";

// Format an option leg/trade as "AAPL 250 CALL 8/15"
const formatOptionLabel = (t) => {
  if (!t.strikePrice && !t.strike) return null;
  const strike = t.strikePrice ?? t.strike;
  const expiry = t.expirationDate ?? t.expiration;
  const expiryLabel = expiry
    ? new Date(expiry).toLocaleDateString("en-US", { month: "numeric", day: "numeric" })
    : null;
  return `${strike} ${t.optionType || ""}${expiryLabel ? ` ${expiryLabel}` : ""}`.trim();
};

// Helper to get color/icon based on emotion string
const getEmotionConfig = (emotion) => {
  const config = {
    zen: { color: "text-blue-400 bg-blue-500/10 border-blue-500/20", icon: Smile },
    focused: { color: "text-purple-400 bg-purple-500/10 border-purple-500/20", icon: Target },
    frustrated: { color: "text-red-400 bg-red-500/10 border-red-500/20", icon: AlertTriangle },
    anxious: { color: "text-orange-400 bg-orange-500/10 border-orange-500/20", icon: AlertTriangle },
    confident: { color: "text-yellow-400 bg-yellow-500/10 border-yellow-500/20", icon: TrendingUp },
  };
  return config[emotion?.toLowerCase()] || { color: "text-slate-400 bg-slate-500/10 border-slate-500/20", icon: Clock };
};

// Compact card for the trade attached to an entry (single or multi-leg).
const TradeCard = ({ trade }) => {
  const isOption = trade.securityType === "OPTION" || !!trade.optionType;
  const optionLabel = isOption ? formatOptionLabel(trade) : null;
  const isClosed = trade.status === "closed" || trade.netPnl != null;
  const legs = trade.legs || [];
  const isMultiLeg = legs.length > 1;
  const pnl = trade.netPnl;

  return (
    <div className="mb-3 rounded-lg border border-slate-700 bg-slate-900/40 p-3">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="font-mono font-bold text-slate-200 text-sm">
            {trade.underlyingSymbol || trade.symbol}
          </span>
          {optionLabel && (
            <span className={`text-xs px-1.5 py-0.5 rounded border font-medium ${
              trade.optionType === "PUT"
                ? "bg-orange-500/10 text-orange-400 border-orange-500/20"
                : "bg-blue-500/10 text-blue-400 border-blue-500/20"
            }`}>
              {optionLabel}
            </span>
          )}
          <span className={`text-xs px-1.5 py-0.5 rounded font-bold ${
            trade.side === "SELL" ? "text-red-400" : "text-emerald-400"
          }`}>
            {trade.side} {trade.quantity}
          </span>
          {trade.strategy && (
            <span className="text-xs px-1.5 py-0.5 rounded bg-indigo-500/10 text-indigo-400 border border-indigo-500/20">
              {trade.strategy}
            </span>
          )}
        </div>
        <span className={`text-[10px] font-bold uppercase px-1.5 py-0.5 rounded ${
          isClosed ? "bg-slate-700 text-slate-300" : "bg-emerald-500/10 text-emerald-400"
        }`}>
          {isClosed ? "Closed" : "Open"}
        </span>
      </div>

      <div className="flex items-center gap-3 mt-2 text-xs text-slate-400 font-mono flex-wrap">
        <span>Entry ${Number(trade.entryPrice).toFixed(2)}</span>
        {trade.exitPrice != null && (
          <>
            <span className="text-slate-600">→</span>
            <span>Exit ${Number(trade.exitPrice).toFixed(2)}</span>
          </>
        )}
        {trade.takeProfit != null && (
          <span className="text-amber-400">🎯 Target ${Number(trade.takeProfit).toFixed(2)}</span>
        )}
        {trade.cost != null && <span className="text-slate-500">Cost ${Number(trade.cost).toFixed(2)}</span>}
        {isClosed && pnl != null && (
          <span className={`ml-auto flex items-center gap-1 font-bold ${pnl >= 0 ? "text-emerald-400" : "text-red-400"}`}>
            {pnl >= 0 ? <TrendingUp size={12} /> : <TrendingDown size={12} />}
            {pnl >= 0 ? "+" : ""}{Number(pnl).toFixed(2)}
          </span>
        )}
      </div>

      {isMultiLeg && (
        <div className="mt-2 pt-2 border-t border-slate-800 space-y-1">
          {legs.map((leg) => {
            const legOptionLabel = formatOptionLabel(leg);
            return (
              <div key={leg.id} className="flex items-center gap-2 text-xs text-slate-500 font-mono flex-wrap">
                <span className={leg.side === "SELL" ? "text-red-400" : "text-emerald-400"}>
                  {leg.side} {leg.quantity}
                </span>
                {legOptionLabel && <span>{legOptionLabel}</span>}
                <span>@ ${Number(leg.entryPrice).toFixed(2)}</span>
                {leg.exitPrice != null && <span>→ ${Number(leg.exitPrice).toFixed(2)}</span>}
                {leg.takeProfit != null && <span className="text-amber-400">🎯 {Number(leg.takeProfit).toFixed(2)}</span>}
                {leg.netPnl != null && (
                  <span className={leg.netPnl >= 0 ? "text-emerald-400" : "text-red-400"}>
                    {leg.netPnl >= 0 ? "+" : ""}{Number(leg.netPnl).toFixed(2)}
                  </span>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

const JournalEntryCard = ({ entry, onDelete, onUpdate }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [addingNote, setAddingNote] = useState(false);
  const emotionConfig = getEmotionConfig(entry.emotionalState);
  const EmotionIcon = emotionConfig.icon;

  const handleEditSubmit = async (updates) => {
    // onUpdate returns true on success; close the editor when it does.
    const ok = onUpdate ? await onUpdate(entry.id, updates) : false;
    if (ok) {
      setIsEditing(false);
      setAddingNote(false);
    }
  };

  if (isEditing || addingNote) {
    return (
      <div className="bg-slate-800/50 border border-indigo-500/40 p-5 rounded-xl">
        <div className="flex items-center justify-between mb-3">
          <span className="text-xs font-semibold text-indigo-400 uppercase tracking-wider">
            {addingNote ? "Add Note" : "Editing Entry"}
          </span>
          <button
            onClick={() => { setIsEditing(false); setAddingNote(false); }}
            className="text-slate-500 hover:text-slate-300 transition-colors p-1"
            title="Cancel"
          >
            <X size={16} />
          </button>
        </div>
        <JournalQuickNote
          onSubmit={handleEditSubmit}
          initialData={entry}
        />
      </div>
    );
  }

  // Format time (e.g., "10:32 AM")
  const timeString = entry.createdAt 
    ? new Date(entry.createdAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
    : "Just Now";

  // Parse mistakes if string
  const mistakes = entry.mistakesMade ? entry.mistakesMade.split(",").filter(m => m.trim()) : [];
  
  // Parse attachments if present (ensure it's an array)
  // Backend stores JSON string in DB but API might return parsed JSON or string depending on implementation.
  // Our JournalEntry.js JSON.stringify's it. Let's handle both.
  let attachments = [];
  if (Array.isArray(entry.attachments)) {
    attachments = entry.attachments;
  } else if (typeof entry.attachments === 'string') {
    try {
      attachments = JSON.parse(entry.attachments);
    } catch (e) {
      attachments = [];
    }
  }

  // Extract Entry/Exit from note text if present (simple regex or parsing convention)
  // We prepended `[Entry: X | Target: Y]` in JournalInput. Let's trust the text for now
  // or checking for attachments is the main update here.

  return (
    <div className="bg-slate-800/50 border border-slate-700/50 p-5 rounded-xl hover:bg-slate-800 hover:border-slate-600 transition group relative">
      
      {/* Edit / Delete Actions — dimmed at rest, fully visible on hover/focus.
          Kept tappable on touch devices (no opacity-0). */}
      <div className="absolute top-4 right-4 flex items-center gap-1 opacity-60 group-hover:opacity-100 transition-opacity z-10">
        <button
          onClick={() => setIsEditing(true)}
          className="text-slate-500 hover:text-indigo-400 transition-colors p-1"
          title="Edit Entry"
        >
          <Pencil size={16} />
        </button>
        <button
          onClick={() => onDelete(entry.id)}
          className="text-slate-500 hover:text-red-400 transition-colors p-1"
          title="Delete Entry"
        >
          <Trash2 size={16} />
        </button>
      </div>

      {/* Header Row: Time, Setup, Emotion */}
      <div className="flex items-center gap-3 mb-2 flex-wrap">
        <span className="text-xs font-mono text-slate-500">{timeString}</span>
        
        {entry.tradeReason && (
          <span className="text-xs px-2 py-0.5 rounded bg-emerald-500/10 text-emerald-400 font-medium border border-emerald-500/20">
            {entry.tradeReason}
          </span>
        )}

        {entry.emotionalState && (
          <div className={`flex items-center gap-1.5 px-2 py-0.5 rounded text-xs font-medium border ${emotionConfig.color}`}>
             <EmotionIcon size={10} />
             <span className="capitalize">{entry.emotionalState}</span>
          </div>
        )}
      </div>

      {/* Attachments (Charts) */}
      {attachments.length > 0 && (
        <div className="mb-3 flex gap-2 overflow-x-auto">
          {attachments.map((att, i) => (
             <div key={i} className="relative rounded-lg overflow-hidden border border-slate-700 max-h-60 max-w-full">
               {att.type === 'image' || att.mimetype?.startsWith('image') || att.url?.match(/\.(jpeg|jpg|png|webp)$/i) ? (
                 <a href={att.url} target="_blank" rel="noopener noreferrer">
                   <img 
                     src={att.url} 
                     alt="Chart" 
                     className="max-h-60 object-contain hover:scale-105 transition-transform"
                     onError={(e) => { e.target.style.display = 'none'; }} 
                    />
                 </a>
               ) : (
                 <div className="p-4 bg-slate-900 text-slate-500 text-xs">
                   <ImageIcon size={24} className="mb-1" />
                   Attachment
                 </div>
               )}
             </div>
          ))}
        </div>
      )}

      {/* Trade Card */}
      {entry.trade && <TradeCard trade={entry.trade} />}

      {/* Main Content */}
      {entry.notes ? (
        <p className="text-slate-200 leading-relaxed mb-3 whitespace-pre-wrap text-sm">
          {entry.notes}
        </p>
      ) : entry.trade ? (
        <button
          onClick={() => setAddingNote(true)}
          className="flex items-center gap-1.5 text-xs text-slate-500 hover:text-indigo-400 transition-colors mb-3"
        >
          <Plus size={12} /> Add note
        </button>
      ) : null}

      {/* Footer: Mistakes & Confidence */}
      <div className="flex flex-wrap items-center gap-3">
        {mistakes.length > 0 && (
          <div className="flex gap-2">
            {mistakes.map((mistake, i) => (
              <span key={i} className="text-xs text-amber-500/80 bg-amber-500/5 px-1.5 py-0.5 rounded border border-amber-500/10">
                #{mistake.trim()}
              </span>
            ))}
          </div>
        )}

        {/* Confidence dot if high */}
        {entry.confidenceLevel >= 8 && (
          <div className="flex items-center gap-1.5 ml-auto" title="High Confidence">
            <div className="h-1.5 w-1.5 rounded-full bg-emerald-500 shadow-[0_0_8px_rgba(16,185,129,0.5)]"></div>
            <span className="text-xs text-slate-500 font-medium">Confident ({entry.confidenceLevel})</span>
          </div>
        )}
      </div>

    </div>
  );
};

export default JournalEntryCard;
